// Open the signaling socket in parallel with `/v1/connect`.
//
// The signaling URL comes back in the connect response and embeds the
// session id, so the socket's TCP + TLS + upgrade would otherwise start
// only after connect returns. Coord also serves a session-less
// `/v1/signal` that takes the session id in `auth`; once we've seen
// coord's signaling base, later boots dial it before connect resolves.

import type { WebSocketLike } from "../webrtc/signaling.js";

const STORAGE_KEY = "p2claw_signal_base";
const SIGNAL_PATH = "/v1/signal";

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface EarlySignal {
  base: string;
  ws: WebSocketLike;
}

/** `localStorage`, or undefined where it's missing or throws. */
export function safeGetLocalStorage(): StorageLike | undefined {
  try {
    return globalThis.localStorage ?? undefined;
  } catch {
    return undefined;
  }
}

/** `wss://coord.example/v1/signal/<id>` → `wss://coord.example`. */
export function signalBaseOf(signalUrl: string): string | null {
  const i = signalUrl.indexOf(`${SIGNAL_PATH}/`);
  return i > 0 ? signalUrl.slice(0, i) : null;
}

/** Take the socket the wrapper's inline script dialed, once. */
function adoptInlineSignal(): EarlySignal | null {
  const g = globalThis as { __p2clawEarlySignal?: EarlySignal };
  const early = g.__p2clawEarlySignal ?? null;
  delete g.__p2clawEarlySignal;
  return early;
}

/** The inline script's socket, else a fresh dial of the remembered
 *  signaling base, if any. Never throws. */
export function openEarlySignal(
  storage: StorageLike | undefined,
  factory: (url: string) => WebSocketLike = (url) => new WebSocket(url),
): EarlySignal | null {
  const inline = adoptInlineSignal();
  if (inline) return inline;
  let base: string | null = null;
  try {
    base = storage?.getItem(STORAGE_KEY) ?? null;
  } catch {
    return null;
  }
  if (!base || !/^wss?:\/\//.test(base)) return null;
  try {
    return { base, ws: factory(`${base}${SIGNAL_PATH}`) };
  } catch {
    return null;
  }
}

/** Remember coord's signaling base for the next boot. Never throws. */
export function rememberSignalBase(storage: StorageLike | undefined, signalUrl: string): void {
  const base = signalBaseOf(signalUrl);
  if (!base) return;
  try {
    storage?.setItem(STORAGE_KEY, base);
  } catch {
    // Storage disabled or full: next boot just skips the early dial.
  }
}

/**
 * Hand over the early socket if it targets the same coord as
 * `signalUrl`; otherwise close it and return undefined.
 */
export function claimEarlySignal(
  early: EarlySignal | null,
  signalUrl: string,
): WebSocketLike | undefined {
  if (!early) return undefined;
  if (signalBaseOf(signalUrl) === early.base) return early.ws;
  discardEarlySignal(early);
  return undefined;
}

export function discardEarlySignal(early: EarlySignal | null): void {
  try {
    early?.ws.close();
  } catch {
    // Already closed.
  }
}
